/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { REVOKE_BEFORE_APPROVAL_REQUIRED, Token } from "../../../../../store/global/TokenEnum";
import { Amount } from "../../../../../types/amount";
import { PermitType } from "../../../../../utils/permitUtils";
import { useMachine } from "@xstate/react";
import { approveMachine } from "../../../../../utils/permitFSM";
import { GlobalStore } from "../../../../../store/global/GlobalStore";
import { useApproveViaPermits } from "../../../../../utils/abis/usePermits";
import { Hash } from "viem";
import { ConfirmButton } from "../ConfirmButton";
import { ConfirmInWallet } from "../ConfirmInWallet";
import { TryAgain } from "../TryAgain";
import { ProcessingTransaction } from "../ProcessingTransaction";
import { Approved } from "../Approved";
import { RevokeButton } from "../RevokeButton";
import { Text } from "../../../../general/Text";
import { css } from "@emotion/react";
import { ApproveTokenTextContainer } from "./elements/ApproveTokenTextContainer";
import { SpendLimitTooltip } from "../SpendLimitTooltip";
import { ApprovalStore } from "../../../../../store/approvals/ApprovalsStore";
import { useLogger } from "../../../../../utils/logger";

interface ApproveTokenProps {
  symbol: Token;
  tokenIcon: string;
  permitType: PermitType;
  needed: Amount;
  onStatusChange: (status: "DONE" | "CUSTOM_APPROVALS") => void;
}

export const ApprovalRow: React.FC<ApproveTokenProps> = ({
  needed,
  onStatusChange,
  permitType,
  symbol,
  tokenIcon,
}) => {
  const { logger } = useLogger();
  const [currentState, send] = useMachine(approveMachine);
  const [txHash, setTxHash] = useState<Hash | undefined>(undefined);
  const [reApprovedAmount, setReapprovedAmount] = useState<Amount | undefined>(undefined);
  const [hasAnnouncedFinish, setHasAnnouncedFinish] = useState<boolean>(false);
  const hasRequestedApproval = useRef(false);
  const { approve } = useApproveViaPermits(permitType);
  useEffect(() => {
    logger.log(`Current State: ${symbol}:${JSON.stringify(currentState.value)}`);
  }, [logger, currentState, symbol]);
  useEffect(() => {
    if (currentState.matches("HAS_EXISTING_APPROVAL")) {
      const approval = ApprovalStore.getRawState().currentAllowances.find(
        allowance => allowance.symbol === symbol
      )?.allowance;
      if (approval) {
        if (approval.value.gt(0)) {
          send("ON_HAS_EXISTING_APPROVAL");
        } else {
          send("ON_INSUFFICIENT_APPROVAL");
        }
      }
    }
  }, [currentState, symbol, send]);
  useEffect(() => {
    if (currentState.matches("IS_APPROVAL_SUFFICIENT")) {
      const approval = ApprovalStore.getRawState().initialAllowances.find(
        allowance => allowance.symbol === symbol
      )?.allowance;
      const needed = GlobalStore.getRawState().takerTokens.find(e => e.token === symbol)?.amount;
      if (
        approval &&
        needed &&
        approval.value.gt(0) &&
        needed.value.gt(0) &&
        approval.value.gte(needed.value)
      ) {
        send("ON_SUFFICIENT_APPROVAL");
      } else {
        send("ON_INSUFFICIENT_APPROVAL");
      }
    }
  }, [currentState, symbol, send]);

  useEffect(() => {
    if (currentState.matches({ REVOKE: "REVOKE_TX_CONFRIMED" })) {
      send("ON_REVOKED");
    }
  }, [currentState, send]);

  useEffect(() => {
    if (currentState.matches("IS_REVOKE_FIRST_TOKEN")) {
      REVOKE_BEFORE_APPROVAL_REQUIRED.includes(symbol)
        ? send("ON_IS_REVOKE_FIRST")
        : send("ON_IS_NOT_REVOKE_FIRST");
    }
  }, [currentState, symbol, send]);

  const updateStoreApproval = useCallback(
    (newAmount: Amount) => {
      ApprovalStore.update(s => {
        s.currentAllowances = s.currentAllowances.map(allowance => {
          if (allowance.symbol === symbol) {
            return {
              ...allowance,
              allowance: newAmount,
            };
          } else {
            return allowance;
          }
        });
      });
    },
    [symbol]
  );

  const handleRevoke = useCallback(() => {
    const doAsync = async () => {
      setReapprovedAmount(undefined);
      try {
        const SHOULD_REVOKE = true;
        await approve(
          symbol,
          txHash => {
            send("ON_REVOKE_CONFIRM");
            setTxHash(txHash);
          },
          newAmount => {
            hasRequestedApproval.current = false;
            send("ON_TX_RECEIPT");
            updateStoreApproval(newAmount);
          },
          SHOULD_REVOKE
        );
      } catch (e) {
        hasRequestedApproval.current = false;
        logger.log(`Rejected: ${JSON.stringify(e)}`);
        send("ON_REVOKE_REJECT");
      }
    };
    if (!hasRequestedApproval.current) {
      void doAsync();
    }
  }, [logger, updateStoreApproval, approve, symbol, send]);

  const handleApprove = useCallback(() => {
    const doAsync = async () => {
      try {
        await approve(
          symbol,
          txHash => {
            send("ON_CONFIRM");
            setTxHash(txHash);
          },
          newAmount => {
            hasRequestedApproval.current = false;
            logger.log(`Reapproved amount: ${newAmount.value.toString()}`);
            setReapprovedAmount(newAmount);
            updateStoreApproval(newAmount);
            send("ON_TX_RECIEPT");
          }
        );
      } catch (e) {
        hasRequestedApproval.current = false;
        logger.log(`Rejected ${JSON.stringify(e)}`);
        send("ON_APPROVAL_REJECT");
      }
    };
    if (!hasRequestedApproval.current) {
      hasRequestedApproval.current = true;
      void doAsync();
    }
  }, [approve, symbol, send, logger, updateStoreApproval]);

  useEffect(() => {
    if (currentState.matches("IS_REAPPROVED_AMOUNT_SUFFICIENT") && reApprovedAmount) {
      logger.log(`reApprovedAmount: ${reApprovedAmount.toString()}`);
      if (reApprovedAmount.value.gte(needed.value)) {
        send("ON_SUFFICIENT_AMOUNT");
        setReapprovedAmount(undefined); // Clean reapproved amount for future states
      } else {
        const reApprovedAmountString = reApprovedAmount.value.toString() ?? "";
        logger.log(`Approved for ${reApprovedAmountString} but needed ${needed.value.toString()}`);
        send("ON_INSUFFICIENT_APPROVAL");
      }
    }
  }, [logger, currentState, needed.value, reApprovedAmount, send]);

  useEffect(() => {
    if (currentState.matches({ APPROVE: "TX_CONFIRMED" })) {
      send("ON_APPROVED");
    }
  }, [currentState, needed.value, send]);

  useEffect(() => {
    if (currentState.matches({ APPROVE: "CONFIRM_IN_WALLET" })) {
      handleApprove();
    }
  }, [currentState, handleApprove]);

  useEffect(() => {
    if (currentState.matches({ REVOKE: "CONFIRM_IN_WALLET" })) {
      handleRevoke();
    }
  }, [currentState, handleRevoke]);

  useEffect(() => {
    if (currentState.matches("DONE") && !hasAnnouncedFinish) {
      setHasAnnouncedFinish(true);
      onStatusChange("DONE");
    }
  }, [currentState, hasAnnouncedFinish, onStatusChange]);

  useEffect(() => {
    logger.log(`User set custom approvals for ${symbol}, restarting process`);
    if (currentState.matches("CUSTOM_APPROVALS")) {
      onStatusChange("CUSTOM_APPROVALS");
      send("ON_RESTART");
    }
  }, [currentState, logger, onStatusChange, send, symbol]);

  const currentAllowance = ApprovalStore.getRawState().currentAllowances.find(allowance => {
    return allowance.symbol === symbol;
  });

  return (
    <>
      <div key={symbol} className="row g-0 align-items-center">
        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center">
            <img src={tokenIcon} style={{ marginRight: "10px" }} alt={`${symbol} symbol`} />
          </div>

          <ApproveTokenTextContainer>
            <span
              css={css`
                display: inline-block;
                margin-top: 2px;
              `}
            >
              <Text textStyle={"Paragraph"} size={"XL"} shouldScale={false}>
                {symbol}
              </Text>
            </span>
            <div style={{ display: "flex" }}>
              <SpendLimitTooltip
                isVisible={
                  currentState.matches({ APPROVE: "PENDING_APPROVAL" }) &&
                  currentAllowance &&
                  currentAllowance.allowance.value.gt(0)
                }
                currentAllowance={currentAllowance?.allowance ?? Amount.ZERO}
                needed={needed}
                symbol={symbol}
              />
              <ConfirmButton
                text={"Approve"}
                isVisible={currentState.matches({ APPROVE: "PENDING_APPROVAL" })}
                onClick={() => {
                  send("ON_APPROVAL_START");
                }}
              />
              <ConfirmInWallet
                isVisible={
                  currentState.matches({ APPROVE: "CONFIRM_IN_WALLET" }) ||
                  currentState.matches({ REVOKE: "CONFIRM_IN_WALLET" })
                }
                confirmOrApproveText={"Approve"}
              />
              <TryAgain
                isVisible={
                  currentState.matches({ APPROVE: "RETRY_APPROVE" }) ||
                  currentState.matches({ REVOKE: "RETRY_REVOKE" })
                }
                onClick={() => {
                  if (currentState.matches({ APPROVE: "RETRY_APPROVE" })) send("ON_RETRY");
                  if (currentState.matches({ REVOKE: "RETRY_REVOKE" })) send("ON_RETRY_REVOKE");
                }}
              />
              <ProcessingTransaction
                isVisible={
                  currentState.matches({ APPROVE: "WAITING_FOR_TX" }) ||
                  currentState.matches({ REVOKE: "WAITING_FOR_TX" })
                }
                tx={txHash?.toString() ?? ""}
              />
              <Approved isVisible={currentState.matches("DONE")} tx={txHash?.toString() ?? ""} />
              <RevokeButton
                isVisible={currentState.matches({ REVOKE: "PENDING_REVOKE" })}
                onClick={() => {
                  send("ON_REVOKE_START");
                }}
              />
            </div>
          </ApproveTokenTextContainer>
        </div>
      </div>
    </>
  );
};
